import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

export interface IPendingLocation {
  pathname: string
  search: string
  hash: string
  /** How the blocked transition was made, so confirming it does not turn a replace into a push. */
  action: 'PUSH' | 'REPLACE' | 'POP'
}

export interface IUseUnsavedChangesGuard {
  /** Where the user tried to go, or null when nothing is being held back. */
  pendingLocation: IPendingLocation | null
  /** Discard the changes and go. */
  confirmNavigation: () => void
  /** Stay put. */
  cancelNavigation: () => void
}

/**
 * Holds back route navigation while an editor has unsaved changes, and warns on browser close.
 *
 * react-router v5, so this is `history.block` - `useBlocker` does not exist until v6.4. Blocking is
 * route-level, which is the point: anything expressed as a URL, tab segments included, is caught
 * here rather than needing a second pending-state alongside it.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean, beforeUnloadMessage: string): IUseUnsavedChangesGuard {
  const history = useHistory()
  const [pendingLocation, setPendingLocation] = useState<IPendingLocation | null>(null)
  // Waved through for exactly one transition. Unblocking instead would leave the guard off unless
  // the caller happened to clear the dirty flag in the same render.
  const allowNext = useRef(false)

  useEffect(() => {
    return history.block((location, action) => {
      if (allowNext.current) {
        allowNext.current = false
        return undefined
      }
      if (hasUnsavedChanges) {
        setPendingLocation({ pathname: location.pathname, search: location.search, hash: location.hash, action })
        return false
      }
      return undefined
    })
  }, [hasUnsavedChanges, history])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = beforeUnloadMessage
        return beforeUnloadMessage
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges, beforeUnloadMessage])

  const confirmNavigation = () => {
    const target = pendingLocation
    setPendingLocation(null)
    if (!target) return
    allowNext.current = true
    const to = `${target.pathname}${target.search}${target.hash}`
    if (target.action === 'REPLACE') {
      history.replace(to)
      return
    }
    // Known limitation for POP: history reverts a blocked Back and does not tell a blocker how far
    // it was going, so this replays it as a push. The user lands on the right URL, but the entry
    // they came from stays on the stack. Fixing it properly means letting history run the original
    // transition through the Router's getUserConfirmation rather than replaying it here.
    history.push(to)
  }

  return { pendingLocation, confirmNavigation, cancelNavigation: () => setPendingLocation(null) }
}
